import { type Dictionary, type EntityState } from '@ngrx/entity';
import { type Draft, produce } from 'immer';
import { type PartialDeep } from 'type-fest';

import {
  type CanvasElement,
  type CanvasElementId,
  type CanvasElementPathNode,
  type CanvasElementType,
  type CanvasNamespaceId,
  type ElementTargetPath,
  type PropsOf,
  type SizeSettings,
  type WithSlots,
} from '@blaze-runtime/engine-domain';
import { getNewElementId } from '@blaze-runtime/engine-domain';
import { deepClone } from '@blaze-shared/shared-util';
import { type StyleSheet } from '@blaze-shared/theming-domain';

import { type CanvasElementDefinitionInternal } from '../../canvas-elements/element-definition';
import { stripClassTypes } from '../../canvas-elements/element-definition';
import { hasSlots } from '../../canvas-elements/element-model/element-props-guards';
import {
  isFormGroup,
  isVariantOfFormContainer,
} from '../../canvas-elements/element-model/element-type-guards';
import { getBreakLineDefinition } from '../../elements/break-line/break-line.element';
import { CONTAINER_INITIAL_SLOT_ID } from '../../elements/container/_container.models';
import { getContainerDefinition } from '../../elements/container/container.definition';
import { getStyleSheetForNewElement, setStyleSheetToElementMutably } from '../../services/styles';

export type PlacementMode = 'normal' | 'placeholder';

/** Style assignment for a newly placed element. */
export interface PlaceElementStyleData {
  /** Name of the style */
  name?: string;
  /** Available style sheets */
  styleSheets: StyleSheet[];
  /** Should assign a default style. If a name is provided it will try to assign it first then try default. */
  useDefault?: boolean;
}

/**
 * Model used to provide info about
 *
 * Either {@link elementDefinition} or {@link elementId} should be used, based on whether a new element is to be placed or an existing one is about to be put in a new place.
 */
export interface PlaceElementData {
  /** The definition of the inserted element */
  elementDefinition?: CanvasElementDefinitionInternal;
  /** Initial props used when creating a new element. These props override the initial props in the element definition. */
  initProps?: PartialDeep<PropsOf<CanvasElementType>>;

  /** The id of an existing element, that's being placed in a new position. Make sure it was previously removed from it's previous parent slot. */
  elementId?: CanvasElementId;

  /** Path at which the element should be placed. */
  atPath?: ElementTargetPath;
  /** By default the element is added in a normal way. Added elements can be marked as placeholders. Used while dragging. */
  mode?: PlacementMode;

  /** Setup desired element size. Use it only when generating layouts. */
  desiredSize?: SizeSettings;

  /** Assign style */
  style?: PlaceElementStyleData;
}

/**
 * Result of placing the element into the canvas.
 * Includes the element id of the placed element.
 */
export interface PlaceElementMutablyResult {
  elementId?: CanvasElementId;
}

export interface PlaceElementResult extends PlaceElementMutablyResult {
  state: EntityState<CanvasElement>;
}

export interface GenerateNewElementData<T extends CanvasElementType> {
  namespaceId: CanvasNamespaceId;
  elementDefinition: CanvasElementDefinitionInternal<T>;
  mode?: PlacementMode;
  initProps?: PartialDeep<PropsOf<T>>;
  initSlotProps?: PartialDeep<PropsOf<T>>;
}

// TODO: test
/**
 * Puts the element at the provided path into the state
 * The same logic is used by the dragging mechanism and the actual element insertion mechanism in order to provide the same results.
 *
 * @param state entity state with CanvasElements (ids and entities)
 * @returns state with the element added at the provided place and the id of the placed element (useful for newly created ones)
 */
export function placeElement(
  state: EntityState<CanvasElement>,
  data: PlaceElementData,
): PlaceElementResult {
  let newElementId: CanvasElementId | undefined;

  const newState = produce(state, (draft) => {
    newElementId = placeElementMutably(draft, data)?.elementId;
  });

  return { state: newState, elementId: newElementId };
}

function getPlacementNamespaceId(
  entities: Dictionary<CanvasElement>,
  atPath: ElementTargetPath,
): CanvasNamespaceId {
  return entities[atPath.id]?.ns || '';
}

/** Mutating version of {@link placeElement}. */
export function placeElementMutably(
  draft: Draft<EntityState<CanvasElement>>,
  {
    atPath,
    elementDefinition,
    elementId,
    mode = 'normal',
    initProps,
    desiredSize,
    style,
  }: PlaceElementData,
): PlaceElementMutablyResult | undefined {
  if (atPath === undefined || (elementDefinition === undefined && elementId === undefined)) {
    return;
  }

  // TODO: moving element from one namespace to another -> update namespace, id and entity [key = <ns>.<innerid>])
  // right now it's done in the reducer as a part of `addExistingElementAtPath` action
  // however, it'd be best to be done here - this function is the most widely used for placing the element
  // REMEMBER, that if the element gets moved to another namespace, the connections should be updated! (namespace and source element.id)
  // IDEA: change namespaces for all affected elements AND return data needed to update connections?
  // Logic implemented in: updateNamespacesAfterMove

  let targetPath = atPath;
  const targetNamespaceId = getPlacementNamespaceId(draft.entities, atPath);

  // Automatically wrap into container
  // TODO: extract the logic to a helper function
  if (
    atPath.targetPosition?.type === 'bottom-new-container' ||
    atPath.targetPosition?.type === 'top-new-container'
  ) {
    const containerElement = generateNewElement(draft, {
      namespaceId: targetNamespaceId,
      elementDefinition: stripClassTypes(getContainerDefinition()),
      mode,
      initProps: undefined,
      initSlotProps: {},
    });

    if (hasSlots(containerElement) && atPath.targetPosition.alignment) {
      const firstColumn = containerElement.slots[CONTAINER_INITIAL_SLOT_ID];
      if (firstColumn) {
        firstColumn.alignment = {
          horizontal: atPath.targetPosition.alignment,
          vertical: 'top',
          syncVerticalLineAlignment: true,
        };
      }
    }

    addEntityMutably(draft, containerElement);
    putElementAtPathMutably(draft, [containerElement.id], {
      ...atPath,
      targetPosition: { type: 'bottom' },
    });

    targetPath = {
      id: containerElement.id,
      slot: CONTAINER_INITIAL_SLOT_ID,
      index: 0,
      parentId: containerElement.id,
      targetPosition: { type: 'left' },
    };
  }

  // Add slot to parent root element when creating a navigation element
  if (atPath.targetPosition?.type === 'new-root-slot') {
    if (!atPath.parentId) {
      throw new Error('No parent was specified');
    }

    const rootElement = draft.entities[atPath.parentId];

    if (!rootElement) {
      throw new Error('No parent element was found.');
    }

    if (rootElement.slots && !rootElement.slots?.[atPath.slot]) {
      rootElement.slots.ids.push(atPath.slot);
      rootElement.slots[atPath.slot] = {
        children: [],
        sizing: {},
      };
    }
  }

  let affectedElement: CanvasElement | undefined;
  if (elementId !== undefined) {
    const existingElement = elementId !== undefined ? draft.entities[elementId] : undefined;
    if (existingElement) {
      existingElement.placeholderMode = mode === 'placeholder' ? 'target' : undefined;
    }
    affectedElement = existingElement;
  }

  if (elementDefinition) {
    const newElement = generateNewElement(draft, {
      namespaceId: targetNamespaceId,
      elementDefinition,
      mode,
      initProps,
    });

    // If element is placed inside of form container - use full width by default
    if (
      atPath.parentId &&
      (isVariantOfFormContainer(draft.entities[atPath.parentId]) ||
        isFormGroup(draft.entities[atPath.parentId]))
    ) {
      newElement.width.mode = 'full';
    }

    addEntityMutably(draft, newElement);
    elementId = newElement.id;
    affectedElement = newElement;
  }

  if (elementId === undefined) {
    throw new Error('Element id undefined');
  }

  if (affectedElement) {
    if (desiredSize) {
      affectedElement.width = desiredSize.width || affectedElement.width;
      affectedElement.height = desiredSize.height || affectedElement.height;

      // Try to squeeze element to fit in slot / line
    } else if (targetPath.fit) {
      const { fit } = targetPath;
      const { value = 0 } = affectedElement.width;

      if (fit?.maxWidth && value >= fit.maxWidth) {
        const result: number | undefined = Math.min(value, fit.maxWidth);
        if (result !== undefined && result > 0) {
          affectedElement.width.value = Math.floor(result);
        }
      }
    }
  }
  putElementAtPathMutably(draft, [elementId], targetPath);
  addBreakLinesMutably(draft, targetNamespaceId, elementId, targetPath);
  sanitizeBreakLinesMutably(draft, targetPath.parentId, targetPath.slot);

  if (style && elementDefinition) {
    const styleSheet = getStyleSheetForNewElement(
      elementDefinition.type,
      style.styleSheets,
      style.name,
      style.useDefault,
    );

    if (styleSheet) {
      setStyleSheetToElementMutably({
        entities: draft.entities,
        elementId,
        styleSheet,
        elementDefinition,
      });
    }
  }

  return { elementId };
}

// TODO: test
export function generateNewElement<T extends CanvasElementType>(
  state: EntityState<CanvasElement>,
  {
    namespaceId,
    elementDefinition,
    mode = 'normal',
    initProps,
    initSlotProps,
  }: GenerateNewElementData<T>,
): CanvasElement {
  const id = getNewElementId(namespaceId);
  return {
    id,
    ns: namespaceId,
    name: findNameForElement(state, namespaceId, elementDefinition.type, elementDefinition.name),
    placeholderMode: mode === 'placeholder' ? 'target' : undefined,
    type: elementDefinition.type,
    width: { ...elementDefinition.width },
    height: elementDefinition.height,
    slots: generateSlots(elementDefinition, initSlotProps),
    version: elementDefinition.version,
    props: { ...(initProps || elementDefinition.initial) },
  } as CanvasElement;
}

/**
 * Returns first free element id within provided namespace
 * @param namespaceId the id of the namespace
 * @param idsCount the number of ids to generate
 * @returns the id of the element
 */
// TODO: test
export function calculateManyNewElementIds(
  namespaceId: CanvasNamespaceId,
  idsCount = 1,
): CanvasElementId[] {
  return Array.from({ length: idsCount }).map(() => {
    return getNewElementId(namespaceId);
  });
}

/**
 * Returns a new name for an element of provided type within provided namespace
 * @param state the state with element entities
 * @param namespaceId the id of the namespace
 * @param type the type of the element
 * @param name the name of the element (from the definition)
 * @returns the id of the element
 */
// TODO: test
export function findNameForElement(
  state: EntityState<CanvasElement>,
  namespaceId: CanvasNamespaceId,
  type: CanvasElementType,
  name: string,
): string {
  const elementsOfType = (state.ids as CanvasElementId[]).filter(
    (id) => state.entities[id]?.ns === namespaceId && state.entities[id]?.type === type,
  ).length;

  // TODO: fix! this might cause issues! if there are buttons 1, 2, 3 and 1+3 would be removed, a new button would be named 'Button 2' (collision!)
  return `${name} ${elementsOfType + 1}`;
}

// TODO: test
function addEntityMutably(draft: Draft<EntityState<CanvasElement>>, entity: CanvasElement): void {
  const key = entity.id;

  if (key in draft.entities) {
    return;
  }

  (draft.ids as CanvasElementId[]).push(key);
  draft.entities[key] = entity;
}

// TODO: test
function removeEntitiesMutably(
  draft: Draft<EntityState<CanvasElement>>,
  keys: CanvasElementId[],
): void {
  const hasRemoved =
    keys.filter((key) => key in draft.entities).map((key) => delete draft.entities[key]).length > 0;

  if (hasRemoved) {
    draft.ids = (draft.ids as CanvasElementId[]).filter((id) => id in draft.entities);
  }
}

// TODO: test
function putElementAtPathMutably(
  draft: Draft<EntityState<CanvasElement>>,
  elementIds: CanvasElementId[],
  atPath: ElementTargetPath,
): void {
  if (atPath.parentId === undefined || atPath.index === undefined) {
    throw new Error('Parent path not provided');
  }

  const target = draft.entities[atPath.parentId];
  if (target === undefined) {
    throw new Error('Could not find parent element');
  }

  if (hasSlots(target)) {
    const slot = target.slots[atPath.slot];
    if (!slot) {
      throw new Error('Could not find slot');
    }
    slot.children.splice(atPath.index, 0, ...elementIds);
    for (const id of elementIds) {
      const element = draft.entities[id];
      if (!element) {
        throw new Error('Could not find element');
      }
      element.parentNode = { id: atPath.parentId, slot: atPath.slot };
    }
  }
}

// TODO: test
function addBreakLinesMutably(
  draft: Draft<EntityState<CanvasElement>>,
  namespaceId: CanvasNamespaceId,
  newElementId: CanvasElementId,
  atPath: ElementTargetPath,
): void {
  if (atPath.targetPosition?.type !== 'top' && atPath.targetPosition?.type !== 'bottom') {
    return;
  }
  if (atPath.parentId === undefined) {
    throw new Error('Parent id not provided');
  }

  const parentElement = draft.entities[atPath.parentId];
  if (!hasSlots(parentElement)) {
    throw new Error('Parent element has no slots');
  }

  const slotChildren = parentElement.slots[atPath.slot]?.children || [];
  const currentElementIndex = slotChildren.findIndex((id) => id === newElementId);
  const shouldAddBefore =
    currentElementIndex > 0 &&
    draft.entities[slotChildren[currentElementIndex - 1]]?.type !== 'breakLine';
  const shouldAddAfter =
    currentElementIndex < slotChildren.length - 1 &&
    draft.entities[slotChildren[currentElementIndex + 1]]?.type !== 'breakLine';

  const parentNode: CanvasElementPathNode = { id: atPath.parentId, slot: atPath.slot };

  if (shouldAddAfter) {
    const newElement = generateNewElement(draft, {
      namespaceId,
      elementDefinition: stripClassTypes(getBreakLineDefinition()),
    });
    newElement.parentNode = parentNode;
    addEntityMutably(draft, newElement);

    const slot = getElementWithSlots(draft.entities, atPath.parentId).slots[atPath.slot];
    if (!slot) {
      throw new Error('Could not find slot');
    }
    // Add breakline after current element
    slot.children.splice(currentElementIndex + 1, 0, newElement.id);
  }

  if (shouldAddBefore) {
    const newElement = generateNewElement(draft, {
      namespaceId,
      elementDefinition: stripClassTypes(getBreakLineDefinition()),
    });
    newElement.parentNode = parentNode;
    addEntityMutably(draft, newElement);

    const slot = getElementWithSlots(draft.entities, atPath.parentId).slots[atPath.slot];
    if (!slot) {
      throw new Error('Could not find slot');
    }

    // Add breakline in place of current element (moving current element further)
    slot.children.splice(currentElementIndex, 0, newElement.id);
  }
}

// TODO: test
function sanitizeBreakLinesMutably(
  draft: Draft<EntityState<CanvasElement>>,
  elementId?: CanvasElementId,
  slotId?: string,
): void {
  if (elementId === undefined || slotId === undefined) {
    return;
  }

  const toRemove = getBreakLineIdsToRemove(draft, elementId, slotId);
  removeEntitiesMutably(draft, toRemove);

  const element = getElementWithSlots(draft.entities, elementId);
  const slot = element.slots[slotId];
  if (!slot) {
    throw new Error('Could not find slot');
  }

  slot.children = slot.children.filter((id) => !toRemove.includes(id));
}

// TODO: test
function getBreakLineIdsToRemove(
  state: EntityState<CanvasElement>,
  elementId: CanvasElementId,
  slotId: string,
): CanvasElementId[] {
  const toRemove: CanvasElementId[] = [];
  const slot = getElementWithSlots(state.entities, elementId).slots[slotId];
  if (!slot) {
    throw new Error('Could not find slot');
  }
  const childIds = slot.children || [];

  // Find trailing break lines
  let lastNonBreakLineIndex: number = childIds.length;

  for (let i = childIds.length - 1; i >= 0; i--) {
    const id = childIds[i];
    if (state.entities[id]?.type === 'breakLine') {
      toRemove.push(id);
    } else {
      lastNonBreakLineIndex = i;
      break;
    }
  }

  // Find initial and duplicate break lines
  let initial = true;

  for (const [index, id] of childIds.entries()) {
    const element = state.entities[id];

    if (index >= lastNonBreakLineIndex) {
      // The algorithm has already analyzed these indexes
      break;
    }

    if (element?.type === 'breakLine') {
      if (initial) {
        // Remove initial breaklines (they don't change anything)
        toRemove.push(id);
      } else if (state.entities[childIds[index - 1]]?.type === 'breakLine') {
        // Previous element is alos a break line - remove duplicate break line
        toRemove.push(id);
      }
    } else {
      initial = false;
    }
  }

  return toRemove;
}

// TODO: test
function getElementWithSlots(
  elements: Dictionary<CanvasElement>,
  id?: CanvasElementId,
): CanvasElement & WithSlots<string> {
  if (id === undefined) {
    throw new Error('Id not provided');
  }

  const element = elements[id];

  if (element === undefined || !hasSlots(element)) {
    throw new Error('Could not identify element');
  }

  return element;
}

/**
 * Creates slots initial definition.
 * Copies initial slot props from definition to every slot.
 * Used mostly by container element.
 * @param elementDef
 * @param initSlotProps
 */
function generateSlots<T extends CanvasElementType>(
  elementDef: CanvasElementDefinitionInternal<T>,
  initSlotProps?: PartialDeep<PropsOf<T>>,
): CanvasElement['slots'] | undefined {
  if (!elementDef.slots?.ids.length) {
    return elementDef.slots;
  }

  const slots: CanvasElement['slots'] = {
    ids: [...elementDef.slots.ids],
  };

  elementDef.slots.ids.forEach((id) => {
    slots[id] = { children: [] };

    const slot = elementDef.slots?.[id];

    if (slot) {
      slots[id] = {
        ...slots[id],
        ...deepClone(slot),
        props: {
          ...(initSlotProps || elementDef.slotInitial),
        },
      };
    }
  });

  return slots;
}
